import React from 'react'
import { useExportSummaryMutation } from '../../queries/missions'
import { Columns, Dropdown, Element, Icon, Message } from 'react-bulma-components'
import { ExportSummaryParams } from '../../api/missions'
import { DownloadIcon } from '@heroicons/react/outline'
import useFileDownload from '../../hooks/use-file-download'
import useXlsxDownload from '../../hooks/use-xlsx-download'
import Modal, { useModal } from '../modal'
import { Field, Form, Formik } from 'formik'
import InputField from '../form/fields/input'
import SelectField from '../form/fields/select'
import {
  dateToDateInputValue,
  formatCompleteDate,
  formatISODate,
  startOfWeek,
  endOfWeek,
  oneDay,
} from '../../utils/date'
import { RequestButton } from '../request-components/request-components'

interface ExportButtonProps {
  filters: Omit<ExportSummaryParams, 'type'>
  hideCustom?: boolean
}

interface ExportFormValues {
  from: string
  to: string
  type: 'xls' | 'csv' | ''
}

const MAX_EXPORT_RANGE_DAYS = 62

/**
 * Determine whether the selected date interval aligns with full Monday-to-Sunday weeks.
 * Missing dates are treated as aligned to avoid blocking the user before they finish their input.
 * @param from - ISO date string representing the start date chosen by the user
 * @param to - ISO date string representing the end date chosen by the user
 * @returns true if both dates align with the beginning and end of the corresponding weeks
 */
const areDatesAlignedWithFullWeeks = (from?: string, to?: string): boolean => {
  // Ensure the start date matches the Monday of its week when provided
  const fromIsMonday = !from || formatISODate(startOfWeek(new Date(from))) === from
  // Ensure the end date matches the Sunday of its week when provided
  const toIsSunday = !to || formatISODate(endOfWeek(new Date(to))) === to

  return fromIsMonday && toIsSunday
}

const ExportButton: React.FC<ExportButtonProps> = ({ filters, hideCustom = false }) => {
  const exportSummaryMutation = useExportSummaryMutation()
  const modal = useModal()

  const [exportType, setExportType] = React.useState<'xls' | 'csv' | 'custom'>()

  React.useEffect(() => {
    if (exportType && exportType !== 'custom') {
      exportSummaryMutation.mutate({
        ...filters,
        type: exportType,
        //@ts-expect-error
        start: formatISODate(startOfWeek(new Date(filters.start))),
        //@ts-expect-error
        end: formatISODate(endOfWeek(new Date(filters.start))),
      })
    }
  }, [exportType])

  //hook to download csv query result (if export type === csv)
  useFileDownload(
    `teamtim-export.csv`,
    exportSummaryMutation.variables?.type === 'csv' ? exportSummaryMutation.data?.data : undefined,
  )

  //hook to download xls query result (if export type === xls)
  useXlsxDownload(
    `teamtim-export.xlsx`,
    //@ts-expect-error
    exportSummaryMutation.variables?.type === 'xls' ? exportSummaryMutation.data?.data : undefined,
  )

  React.useEffect(() => {
    if (exportSummaryMutation.status === 'success') {
      setExportType(undefined)
      exportSummaryMutation.reset()
    }
  }, [exportSummaryMutation.status])

  return (
    <>
      <Modal actions={modal} title="Export personnalisé" dataTest="export-custom-modal">
        <Formik<ExportFormValues>
          initialValues={{
            from: dateToDateInputValue(
              startOfWeek(filters.start ? new Date(filters.start) : new Date()),
            ),
            to: dateToDateInputValue(
              endOfWeek(filters.start ? new Date(filters.start) : new Date()),
            ),
            type: '',
          }}
          onSubmit={values => {
            exportSummaryMutation.mutate({
              ...filters,
              type: values.type as 'xls' | 'csv',
              //@ts-expect-error - API expects ISO strings even though typing mentions Date
              start: values.from,
              //@ts-expect-error - API expects ISO strings even though typing mentions Date
              end: values.to,
            })
          }}
          validate={values => {
            const errors: Record<keyof ExportFormValues, string> = {} as Record<
              keyof ExportFormValues,
              string
            >

            // Basic required field checks
            if (!values.type) errors.type = 'Champ requis'
            if (!values.from) errors.from = 'Champ requis'
            if (!values.to) errors.to = 'Champ requis'

            return errors
          }}
        >
          {props => {
            const selectedStartDate = props.values.from ? new Date(props.values.from) : undefined
            const selectedEndDate = props.values.to ? new Date(props.values.to) : undefined

            // Check if the selected interval covers complete weeks (Monday to Sunday)
            const isAlignedWithFullWeeks = areDatesAlignedWithFullWeeks(
              props.values.from,
              props.values.to,
            )
            const hasSelectedRange = Boolean(selectedStartDate && selectedEndDate)

            return (
              <Form>
                <Columns>
                  <Columns.Column>
                    <Field
                      label="Du"
                      name="from"
                      type="date"
                      component={InputField}
                      max={props.values.to && dateToDateInputValue(new Date(props.values.to))}
                      min={
                        selectedEndDate
                          ? // Keep the start date within the MAX_EXPORT_RANGE_DAYS window
                            dateToDateInputValue(
                              new Date(
                                selectedEndDate.getTime() - (MAX_EXPORT_RANGE_DAYS - 1) * oneDay,
                              ),
                            )
                          : undefined
                      }
                      help={`2 mois maximum`}
                    />
                  </Columns.Column>
                  <Columns.Column>
                    <Field
                      label="Au"
                      name="to"
                      type="date"
                      min={props.values.from && dateToDateInputValue(new Date(props.values.from))}
                      max={
                        selectedStartDate
                          ? // Keep the end date within the MAX_EXPORT_RANGE_DAYS window
                            dateToDateInputValue(
                              new Date(
                                selectedStartDate.getTime() + (MAX_EXPORT_RANGE_DAYS - 1) * oneDay,
                              ),
                            )
                          : undefined
                      }
                      component={InputField}
                    />
                  </Columns.Column>
                </Columns>
                <Columns>
                  <Columns.Column>
                    <Field
                      label="Type de fichier"
                      name="type"
                      component={SelectField}
                      options={[
                        { value: 'xls', label: 'Excel' },
                        { value: 'csv', label: 'CSV' },
                      ]}
                    />
                  </Columns.Column>
                </Columns>
                <Columns>
                  <Columns.Column>
                    {hasSelectedRange && (
                      <Message mb={4} color="info">
                        <Message.Body>
                          Export du {selectedStartDate ? formatCompleteDate(selectedStartDate) : ''}
                          {' au '}
                          {selectedEndDate ? formatCompleteDate(selectedEndDate) : ''}
                        </Message.Body>
                      </Message>
                    )}
                    {!isAlignedWithFullWeeks && (
                      <Message color="warning" mb={4}>
                        <Message.Body>
                          <p>
                            <Element renderAs="strong">Attention</Element> : les dates sélectionnées
                            ne couvrent pas des semaines complètes du lundi au dimanche. Le calcul
                            des règles employeurs à la semaine peut être tronqué si des jours
                            manquent.
                          </p>
                        </Message.Body>
                      </Message>
                    )}
                    <RequestButton
                      color="primary"
                      mutation={exportSummaryMutation}
                      disabled={exportSummaryMutation.isLoading}
                    >
                      Exporter
                    </RequestButton>
                    {exportSummaryMutation.isError && (
                      <Element textColor="danger">Aucune donnée à exporter</Element>
                    )}
                  </Columns.Column>
                </Columns>
              </Form>
            )
          }}
        </Formik>
      </Modal>
      <Dropdown
        closeOnSelect
        color="primary"
        icon={
          <Icon>
            <DownloadIcon />
          </Icon>
        }
        label="Export"
        onChange={(type: 'csv' | 'xls' | 'custom') => {
          if (type === 'custom') modal.setIsDisplayed(true)
          else setExportType(type)
        }}
      >
        <Dropdown.Item renderAs="a" value="csv">
          CSV
        </Dropdown.Item>
        <Dropdown.Item renderAs={'a'} value="xls">
          Excel
        </Dropdown.Item>
        {!hideCustom ? (
          <Dropdown.Item renderAs="a" value="custom">
            Dates personnalisées
          </Dropdown.Item>
        ) : (
          <></>
        )}
      </Dropdown>
    </>
  )
}

export default ExportButton
